/**
 * Solid hooks for courses domain (Aula) over PowerSync + TanStack Query.
 *
 * Each hook binds canonical `queryKey` (courseQueryKeys) + Drizzle builder,
 * so the view auto-invalidates when dependent tables change.
 * Local-first: all resolves offline from WASQLite. PUBLIC catalog uses
 * REST (server-authoritative) — see `courses/catalog.ts`.
 */

import type { Accessor } from "solid-js";
import { keepPreviousData } from "@tanstack/solid-query";
import type { AbstractPowerSyncDatabase } from "@powersync/common";
import type {
  Block,
  Certificate,
  Course,
  Enrollment,
  LeaderboardEntryRow,
  LearnerStats,
  Lesson,
  LessonProgress,
  PowerSyncDrizzleDB,
  Unit,
} from "@aula/powersync";

import {
  courseQueryKeys,
  getBlocksQuery,
  getCertificateByIdQuery,
  getCourseCertificateQuery,
  getCourseProgressQuery,
  getCourseQuery,
  getEnrollmentQuery,
  getLeaderboardQuery,
  getLearnerStatsQuery,
  getLessonsQuery,
  getMyAuthoredCoursesQuery,
  getMyEnrollmentsQuery,
  getMyOrganizationsQuery,
  getMyReviewProgressQuery,
  getUnitsQuery,
  getUserLeaderboardOptInQuery,
} from "../index";

import {
  createPowerSyncQuery,
  createPowerSyncSingle,
  DEFAULT_THROTTLE_MS,
} from "./create-power-sync-query";

interface BaseOptions {
  db: Accessor<AbstractPowerSyncDatabase | null>;
  drizzle: Accessor<PowerSyncDrizzleDB | null>;
  userId: Accessor<string | undefined>;
  staleTime?: number;
  throttleTime?: number;
}

interface CourseScopedOptions extends Omit<BaseOptions, "userId"> {
  userId: Accessor<string | undefined>;
  courseId: Accessor<string | undefined>;
}

const throttle = (o: { throttleTime?: number }) => o.throttleTime ?? DEFAULT_THROTTLE_MS;

// ─── Authorship ────────────────────────────────────────────────────────────────

export function useMyAuthoredCoursesQuery(options: BaseOptions) {
  return createPowerSyncQuery<Course>({
    queryKey: () => courseQueryKeys.authored(options.userId()),
    db: options.db,
    queryFn: () => {
      const db = options.drizzle();
      const uid = options.userId();
      return db && uid ? getMyAuthoredCoursesQuery(db, uid) : null;
    },
    enabled: () => Boolean(options.userId()) && Boolean(options.drizzle()),
    placeholderData: keepPreviousData,
    staleTime: options.staleTime,
    throttleTime: throttle(options),
    suspense: false,
  });
}

export function useCourseQuery(options: Omit<CourseScopedOptions, "userId">) {
  return createPowerSyncSingle<Course>({
    queryKey: () => courseQueryKeys.tree(options.courseId()),
    db: options.db,
    queryFn: () => {
      const db = options.drizzle();
      const cid = options.courseId();
      return db && cid ? getCourseQuery(db, cid) : null;
    },
    enabled: () => Boolean(options.courseId()) && Boolean(options.drizzle()),
    staleTime: options.staleTime,
    throttleTime: throttle(options),
    suspense: false,
  });
}

export function useUnitsQuery(options: Omit<CourseScopedOptions, "userId">) {
  return createPowerSyncQuery<Unit>({
    queryKey: () => courseQueryKeys.units(options.courseId()),
    db: options.db,
    queryFn: () => {
      const db = options.drizzle();
      const cid = options.courseId();
      return db && cid ? getUnitsQuery(db, cid) : null;
    },
    enabled: () => Boolean(options.courseId()) && Boolean(options.drizzle()),
    placeholderData: keepPreviousData,
    staleTime: options.staleTime,
    throttleTime: throttle(options),
    suspense: false,
  });
}

export function useLessonsQuery(options: Omit<CourseScopedOptions, "userId">) {
  return createPowerSyncQuery<Lesson>({
    queryKey: () => courseQueryKeys.lessons(options.courseId()),
    db: options.db,
    queryFn: () => {
      const db = options.drizzle();
      const cid = options.courseId();
      return db && cid ? getLessonsQuery(db, cid) : null;
    },
    enabled: () => Boolean(options.courseId()) && Boolean(options.drizzle()),
    placeholderData: keepPreviousData,
    staleTime: options.staleTime,
    throttleTime: throttle(options),
    suspense: false,
  });
}

export function useBlocksQuery(options: {
  db: Accessor<AbstractPowerSyncDatabase | null>;
  drizzle: Accessor<PowerSyncDrizzleDB | null>;
  lessonId: Accessor<string | undefined>;
  staleTime?: number;
  throttleTime?: number;
}) {
  return createPowerSyncQuery<Block>({
    queryKey: () => courseQueryKeys.blocks(options.lessonId()),
    db: options.db,
    queryFn: () => {
      const db = options.drizzle();
      const lid = options.lessonId();
      return db && lid ? getBlocksQuery(db, lid) : null;
    },
    enabled: () => Boolean(options.lessonId()) && Boolean(options.drizzle()),
    placeholderData: keepPreviousData,
    staleTime: options.staleTime,
    throttleTime: throttle(options),
    suspense: false,
  });
}

// ─── Consumo ────────────────────────────────────────────────────────────────

export function useMyEnrollmentsQuery(options: BaseOptions) {
  return createPowerSyncQuery<Enrollment>({
    queryKey: () => courseQueryKeys.enrollments(options.userId()),
    db: options.db,
    queryFn: () => {
      const db = options.drizzle();
      const uid = options.userId();
      return db && uid ? getMyEnrollmentsQuery(db, uid) : null;
    },
    enabled: () => Boolean(options.userId()) && Boolean(options.drizzle()),
    placeholderData: keepPreviousData,
    staleTime: options.staleTime,
    throttleTime: throttle(options),
    suspense: false,
  });
}

export function useEnrollmentQuery(options: CourseScopedOptions) {
  return createPowerSyncSingle<Enrollment>({
    queryKey: () => courseQueryKeys.enrollment(options.userId(), options.courseId()),
    db: options.db,
    queryFn: () => {
      const db = options.drizzle();
      const uid = options.userId();
      const cid = options.courseId();
      return db && uid && cid ? getEnrollmentQuery(db, uid, cid) : null;
    },
    enabled: () =>
      Boolean(options.userId()) && Boolean(options.courseId()) && Boolean(options.drizzle()),
    staleTime: options.staleTime,
    throttleTime: throttle(options),
    suspense: false,
  });
}

export function useCourseProgressQuery(options: CourseScopedOptions) {
  return createPowerSyncQuery<LessonProgress>({
    queryKey: () => courseQueryKeys.progress(options.userId(), options.courseId()),
    db: options.db,
    queryFn: () => {
      const db = options.drizzle();
      const uid = options.userId();
      const cid = options.courseId();
      return db && uid && cid ? getCourseProgressQuery(db, uid, cid) : null;
    },
    enabled: () =>
      Boolean(options.userId()) && Boolean(options.courseId()) && Boolean(options.drizzle()),
    placeholderData: keepPreviousData,
    staleTime: options.staleTime,
    throttleTime: throttle(options),
    suspense: false,
  });
}

/** Review progress row (subset of lesson_progress + lesson title). */
export interface ReviewProgressRow {
  lesson_id: string | null;
  course_id: string | null;
  status: string | null;
  score: number | null;
  attempts: number | null;
  completed_at: string | null;
  updated_at: string | null;
  lesson_title: string | null;
}

export function useMyReviewProgressQuery(options: BaseOptions) {
  return createPowerSyncQuery<ReviewProgressRow>({
    queryKey: () => courseQueryKeys.reviewQueue(options.userId()),
    db: options.db,
    queryFn: () => {
      const db = options.drizzle();
      const uid = options.userId();
      return db && uid ? getMyReviewProgressQuery(db, uid) : null;
    },
    enabled: () => Boolean(options.userId()) && Boolean(options.drizzle()),
    placeholderData: keepPreviousData,
    staleTime: options.staleTime,
    throttleTime: throttle(options),
    suspense: false,
  });
}

export function useLearnerStatsQuery(options: BaseOptions) {
  return createPowerSyncSingle<LearnerStats>({
    queryKey: () => courseQueryKeys.learnerStats(options.userId()),
    db: options.db,
    queryFn: () => {
      const db = options.drizzle();
      const uid = options.userId();
      return db && uid ? getLearnerStatsQuery(db, uid) : null;
    },
    enabled: () => Boolean(options.userId()) && Boolean(options.drizzle()),
    staleTime: options.staleTime,
    throttleTime: throttle(options),
    suspense: false,
  });
}

// ─── Certificados (server-emitidos, lectura local-first) ─────────────────────

/** User course certificate (if already issued by server). */
export function useCourseCertificateQuery(options: CourseScopedOptions) {
  return createPowerSyncSingle<Certificate>({
    queryKey: () => courseQueryKeys.certificate(options.userId(), options.courseId()),
    db: options.db,
    queryFn: () => {
      const db = options.drizzle();
      const uid = options.userId();
      const cid = options.courseId();
      return db && uid && cid ? getCourseCertificateQuery(db, uid, cid) : null;
    },
    enabled: () =>
      Boolean(options.userId()) && Boolean(options.courseId()) && Boolean(options.drizzle()),
    staleTime: options.staleTime,
    throttleTime: throttle(options),
    suspense: false,
  });
}

/** Own certificate by id (printable view). */
export function useCertificateByIdQuery(
  options: Omit<BaseOptions, "userId"> & { certId: Accessor<string | undefined> },
) {
  return createPowerSyncSingle<Certificate>({
    queryKey: () => courseQueryKeys.certificateById(options.certId()),
    db: options.db,
    queryFn: () => {
      const db = options.drizzle();
      const id = options.certId();
      return db && id ? getCertificateByIdQuery(db, id) : null;
    },
    enabled: () => Boolean(options.certId()) && Boolean(options.drizzle()),
    staleTime: options.staleTime,
    throttleTime: throttle(options),
    suspense: false,
  });
}

export interface MyOrganizationRow {
  id: string;
  name: string | null;
}

/** My organizations (for leaderboard league selector). */
export function useMyOrganizationsQuery(options: Omit<BaseOptions, "userId">) {
  return createPowerSyncQuery<MyOrganizationRow>({
    queryKey: () => courseQueryKeys.myOrganizations(),
    db: options.db,
    queryFn: () => {
      const db = options.drizzle();
      return db ? getMyOrganizationsQuery(db) : null;
    },
    enabled: () => Boolean(options.drizzle()),
    placeholderData: keepPreviousData,
    staleTime: options.staleTime,
    throttleTime: throttle(options),
    suspense: false,
  });
}

/** User leaderboard consent (privacy opt-in). */
export function useUserLeaderboardOptInQuery(options: BaseOptions) {
  return createPowerSyncSingle<{ leaderboard_opt_in: number | null }>({
    queryKey: () => courseQueryKeys.leaderboardOptIn(options.userId()),
    db: options.db,
    queryFn: () => {
      const db = options.drizzle();
      const uid = options.userId();
      return db && uid ? getUserLeaderboardOptInQuery(db, uid) : null;
    },
    enabled: () => Boolean(options.userId()) && Boolean(options.drizzle()),
    staleTime: options.staleTime,
    throttleTime: throttle(options),
    suspense: false,
  });
}

interface LeaderboardOptions extends Omit<BaseOptions, "userId"> {
  leagueId: Accessor<string>;
}

/**
 * Local-first leaderboard for a league (`'global'` or org id). ONLY
 * contains consented entries (server filters `opted_in`), so non-opted users
 * are never exposed. View derives ranking/tiers via core.
 */
export function useLeaderboardQuery(options: LeaderboardOptions) {
  return createPowerSyncQuery<LeaderboardEntryRow>({
    queryKey: () => courseQueryKeys.leaderboard(options.leagueId()),
    db: options.db,
    queryFn: () => {
      const db = options.drizzle();
      return db ? getLeaderboardQuery(db, options.leagueId()) : null;
    },
    enabled: () => Boolean(options.drizzle()),
    placeholderData: keepPreviousData,
    staleTime: options.staleTime,
    throttleTime: throttle(options),
    suspense: false,
  });
}
